/** @format */

import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  AemPinnedContentResponse,
  DisplayableProductListData,
  DisplayableProductListParams,
  PinnedContent,
  ProductDetail,
  ProductList,
  ProductListCore,
  PzPlpBannerItem,
} from '@plp/data-access/interfaces';
import {
  AnalyticsService,
  FeatureConfigurationService,
  Logger,
  ProductCatalogService,
} from '@shared/data-access/services';
import {
  BeaconQueryParameters,
  CriteoDigitalShelfTalker,
  CriteoPinnedContentSpot,
  CriteoViewProductsResponse,
  HeaderStoreSelection,
  PlpDisplayableProduct,
  PlpInGridSponsoredProduct,
  ProductAvailabilityCollection,
  ProductPromotionCollection,
  ProductPromotionResponse,
  ProductTypes,
  ProductVO,
  PrunedProductListSEO,
} from '@shared/data-access/interfaces';
import {
  CRITEO_CAROUSEL_MINIMUM_PRODUCTS,
  CRITEO_DIGITAL_SHELF_TALKER_POSITION,
  CriteoService,
  DmnAdService,
} from '@shared/data-access/product-recommendations';
import { DEFAULT_PAGE_SIZE, DPLD_KEY, plpDefaultSortMap } from '@plp/data-access/constants';
import { HomrDataService, HomrPage } from '@shared/homr/data-access';
import { Injectable, PLATFORM_ID, TransferState, inject } from '@angular/core';
import {
  Observable,
  catchError,
  combineLatest,
  debounceTime,
  defer,
  distinctUntilChanged,
  from,
  map,
  merge,
  of,
  pairwise,
  skipUntil,
  skipWhile,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';
import {
  ProductListAemService,
  ProductListApiService,
  ProductListMlService,
  ProductListSnackbarService,
  ProductListUIService,
} from '@plp/data-access/services';
import {
  availabilityFilterChanged,
  criteoProductsFetchErrored,
  criteoProductsFetchSuccessful,
  criteoProductsResolvedEmpty,
  displayableProductListBuilt,
  filterChipsClearAllClicked,
  filterContainerClearAllClicked,
  filteringPinnedContentClicked,
  pageNumberChanged,
  pageSizeChanged,
  pinnedContentFetchErrored,
  pinnedContentFetchSuccessful,
  pinnedContentResolvedEmpty,
  plpResolverAPIDataInitialized,
  pzPlpBannerFetchSuccessful,
  pzPlpBannerResolvedEmpty,
  plpResolverOnLoadDataInitialized,
  productAvailabilityFetched,
  productListLoadErrored,
  productListLoadSuccessful,
  productListLoadTriggered,
  productListPromoFetched,
  searchInsteadClicked,
  secondPassProductListFetched,
  selectCriteoProducts,
  selectFeatureFlags,
  selectPageNumber,
  selectPageSize,
  selectPinnedContent,
  selectProductDetails,
  selectProductList,
  selectProductListCore,
  selectProductListFromTransferState,
  selectProductListSEO,
  selectProductListSEOAttributes,
  selectProductVOs,
  selectRefinementUpdateObj,
  selectSearchVO,
  selectSelectedFilters,
  selectSelectedSort,
  selectedFilterRemoved,
  selectedFiltersUpdated,
  sortSelectionChanged,
  srlpResolverAPIDataInitialized,
  srlpResolverOnLoadDataInitialized,
} from '@plp/data-access/state';
import {
  changeHeaderServiceStoreInfo,
  selectPageType,
  selectUserStoreInfo,
  selectUserStoreNumberAndBaseStoreZip,
  setUserStoreInfoFromCookie,
} from '@state/data-access';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

import { CommonPageType } from '@shared/data-access/common';
import { DiscountEligibilityService } from '@shared/data-access/product';
import { PersonalizationDataKeys } from '@shared/data-access/enums';
import { Router } from '@angular/router';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable({ providedIn: 'root' })
export class ProductListEffects {
  private readonly logger = inject(Logger);
  private readonly actions$ = inject(Actions);
  private readonly productCatalogService = inject(ProductCatalogService);
  private readonly discountEligibilityService = inject(DiscountEligibilityService);
  private readonly store = inject(Store);
  private readonly productListApiService = inject(ProductListApiService);
  private readonly productListUIService = inject(ProductListUIService);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly state = inject(TransferState);
  private readonly plpMlService = inject(ProductListMlService);
  private readonly plpSnackbarService = inject(ProductListSnackbarService);
  private readonly criteoService = inject(CriteoService);
  private readonly dmnAdService = inject(DmnAdService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly homrDataService = inject(HomrDataService);
  private readonly featureConfigurationService = inject(FeatureConfigurationService);
  private readonly productListAemService = inject(ProductListAemService);

  private readonly beaconQueryParameters: BeaconQueryParameters = {
    placementType: 'CAROUSEL',
    placementLevel: 'PLACEMENT',
    beaconType: 'LOAD',
  };

  /**
   * @description
   * When product list is updated, get the availability information and set data in state
   */
  updateProductAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        productListLoadSuccessful,
        plpResolverAPIDataInitialized,
        srlpResolverAPIDataInitialized,
        secondPassProductListFetched,
        setUserStoreInfoFromCookie,
        criteoProductsFetchSuccessful,
        criteoProductsFetchErrored,
        criteoProductsResolvedEmpty,
      ),
      concatLatestFrom(() => [
        this.store.select(selectProductVOs),
        this.store.select(selectCriteoProducts),
        this.store.select(selectUserStoreInfo),
      ]),
      switchMap(
        ([, productVOs, criteoProducts, userSelectedStore]: [
          Action,
          ProductVO[],
          CriteoViewProductsResponse | null,
          HeaderStoreSelection | null,
        ]) => this.productAvailability(productVOs, criteoProducts, userSelectedStore),
      ),
      switchMap((productAvailability: ProductAvailabilityCollection | null | []) =>
        of(productAvailabilityFetched({ productAvailability })),
      ),
    ),
  );

  /**
   * @description
   * When product list is updated get promotion information and set data in state
   */
  setPromoMessages$ = createEffect(() =>
    this.store.select(selectProductVOs).pipe(
      skipWhile((productVOs) => !productVOs),
      switchMap((productVOs) => this.productPromotion(productVOs)),
      switchMap((promo: ProductPromotionCollection | ProductPromotionResponse[] | null) =>
        of(
          productListPromoFetched({
            productPromotion: this.discountEligibilityService.generatePromoMessages(promo),
          }),
        ),
      ),
    ),
  );

  /**
   * Listens for any events/actions that should cause us to refresh the product list and fires that
   * refresh.
   */
  triggerProductListUpdate$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(
          searchInsteadClicked,
          sortSelectionChanged,
          filterChipsClearAllClicked,
          filterContainerClearAllClicked,
          pageNumberChanged,
          pageSizeChanged,
          selectedFiltersUpdated,
          selectedFilterRemoved,
          filteringPinnedContentClicked,
          availabilityFilterChanged,
        ),
      ),
      // Trigger refresh when the chosen store changes after the initial fetch
      this.store.select(selectUserStoreNumberAndBaseStoreZip).pipe(
        skipUntil(
          this.actions$.pipe(
            ofType(plpResolverOnLoadDataInitialized, srlpResolverOnLoadDataInitialized),
          ),
        ),
        distinctUntilChanged((a, b) => a.location === b.location && a.zip === b.zip),
        concatLatestFrom(() => this.store.select(selectProductList)),
        // Override with original search term to ensure we keep Search Instead functionality working when store changes
        map(([, productList]) => ({
          type: 'storeInfoChanged' as const,
          searchTerm: productList?.didYouMeanVO?.originalSearchTerm,
        })),
      ),
    ).pipe(
      switchMap((action) => {
        if (
          (action.type === searchInsteadClicked.type || action.type === 'storeInfoChanged') &&
          action.searchTerm
        ) {
          return of(productListLoadTriggered({ searchTerm: action.searchTerm }));
        }
        return of(productListLoadTriggered());
      }),
    ),
  );

  /**
   * @Important
   * @description Listens to productListLoadTriggered action dispatched and updates product list with the new data
   */
  updateProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productListLoadTriggered),
      concatLatestFrom(() => [
        this.store.select(selectProductListCore),
        this.store.select(selectSelectedFilters),
        this.store.select(selectUserStoreInfo),
        this.store.select(selectFeatureFlags),
      ]),
      switchMap(([{ searchTerm }, plc, filters, selectedStoreInfo, featureFlags]) => {
        return this.updateProductList(
          plc,
          filters,
          selectedStoreInfo,
          featureFlags.fulfillmentFacetsEnabled,
          searchTerm,
        );
      }),
      concatLatestFrom(() => this.store.select(selectRefinementUpdateObj)),
      switchMap(([productList, refinementUpdateObj]) => {
        if (!productList) {
          this.plpSnackbarService.openSnackbar(
            'Something went wrong. Please try your last selection again.',
          );
          return of(productListLoadErrored());
        }

        // Set a new ML search event parent id for this new search (post page-load)
        // i.e. filtering, sorting, page change, page size change, etc.
        this.plpMlService.generateNewMlSearchEventParentUUID();

        if (!refinementUpdateObj) {
          return of(productListLoadSuccessful({ productList }));
        }

        const refinementUpdateObjCopy = {
          ...refinementUpdateObj,
          UpdateValue: { ...refinementUpdateObj.UpdateValue },
          ['PLP_event_id']: this.plpMlService.getMlSearchEventParentUUID(),
        };

        refinementUpdateObjCopy.UpdateValue.ProductCountTotal = productList?.totalCount;
        this.analyticsService.triggerAnalyticsEvent('ResultsViewUpdate', refinementUpdateObjCopy);
        return of(productListLoadSuccessful({ productList }));
      }),
    ),
  );

  /**
   * @description Effect to handle setting page query parameters and navigating.
   *
   * This effect listens for actions related to pagination, filtering, and sorting,
   * and updates the query parameters in the URL accordingly.
   *
   * Actions handled: pageNumberChanged, pageSizeChanged, selectedFiltersUpdated, filterChipsClearAllClicked, filterContainerClearAllClicked, sortSelectionChanged
   *
   * It uses the latest values from the store for page number, page size, filters, and selected sort,
   * and calls the `setQueryParamsAndNavigate` method to update the URL.
   *
   * @effect
   * @returns {Observable<null>} An observable that emits `null` and does not dispatch any actions.
   */
  setPageQueryParam$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          availabilityFilterChanged,
          pageNumberChanged,
          pageSizeChanged,
          filterChipsClearAllClicked,
          filterContainerClearAllClicked,
          sortSelectionChanged,
          productListLoadErrored,
          changeHeaderServiceStoreInfo,
          selectedFiltersUpdated,
          selectedFilterRemoved,
        ),
        concatLatestFrom(() => [
          this.store.select(selectPageNumber),
          this.store.select(selectPageSize),
          this.store.select(selectSelectedFilters),
          this.store.select(selectSelectedSort),
          this.store.select(selectProductListSEO),
          this.store.select(selectPageType),
          this.store.select(selectUserStoreInfo),
        ]),
        switchMap(
          ([
            ,
            pageNumber,
            pageSize,
            filters,
            selectedSort,
            productListSEO,
            pageType,
            selectedStore,
          ]: [
            Action,
            number,
            number,
            Record<string, string[]>,
            { label: string; value: number },
            PrunedProductListSEO | null,
            CommonPageType | '',
            HeaderStoreSelection | null,
          ]) => {
            this.setQueryParamsAndNavigate(
              pageNumber,
              pageSize,
              filters,
              selectedSort,
              productListSEO,
              pageType,
              selectedStore,
            );
            return of(null);
          },
        ),
      ),
    { dispatch: false },
  );

  /**
   * Effect to update the displayable product list.
   *
   * This effect listens for changes to the product list, pinned content, or sponsored products.
   * It then combines the latest values from the store for pinned content and sponsored products.
   * If either of these values are missing, it waits until they are present.
   *
   * Once the necessary data is available, it then builds the displayable product list using these
   * values.
   *
   * Finally, it dispatches an action to set the displayable product list data.
   *
   * @returns An observable of the action to set the displayable product list data.
   */
  updateDisplayableProductList$ = createEffect(() =>
    combineLatest([
      this.store.select(selectProductVOs),
      this.store.select(selectPinnedContent),
      this.store.select(selectCriteoProducts),
    ]).pipe(
      pairwise(),
      skipWhile(
        ([, [, pinnedContent, criteoProducts]]) =>
          isPlatformBrowser(this.platformId) && (pinnedContent === null || criteoProducts === null),
      ),
      concatLatestFrom(() => [
        this.store.select(selectSelectedFilters),
        this.store.select(selectPageNumber),
        this.store.select(selectProductDetails),
        this.store.select(selectPageType),
        this.store.select(selectProductListSEO),
        this.store.select(selectFeatureFlags),
        this.store.select(selectUserStoreInfo),
        this.store.select(selectProductListFromTransferState),
      ]),
      map(
        ([
          [[prevVOs, , prevCriteo], [productVOs, pinnedContent, criteoProducts]],
          filters,
          pageNumber,
          productDetails,
          pageType,
          productSEO,
          featureFlags,
          selectedStoreInfo,
          productListFromTransferState,
        ]) => {
          const onlyCriteoChanged = prevCriteo !== criteoProducts && prevVOs === productVOs;
          const adService = this.plpOrSrlpKillswitch(pageType, featureFlags)
            ? this.dmnAdService
            : this.criteoService;

          const seoSponsoredProductsAttrValue = productSEO?.attr?.SPONSORED_PRODUCTS?.value ?? '';
          let sponsoredProducts: PlpInGridSponsoredProduct[] | null = null;
          let sponsoredPinnedContent: CriteoPinnedContentSpot | null = null;
          let criteoGridOnLoadBeacon = '';
          let saleExcludedProductIds: string[] = [];
          if (criteoProducts) {
            if (featureFlags.isDigitalShelfTalkerEnabled && seoSponsoredProductsAttrValue !== 'N') {
              const digitalShelfTalker = criteoProducts.placements.digitalShelfTalker;
              if (digitalShelfTalker) {
                sponsoredPinnedContent = this.prepareSponsoredPinnedContent(
                  adService,
                  digitalShelfTalker,
                );
              }
            }
            const isSalesPage = this.criteoService.isSaleCategory(productSEO?.seoUrl);
            ({ sponsoredProducts, criteoGridOnLoadBeacon, saleExcludedProductIds } =
              this.prepareCriteoGridProducts(
                adService,
                criteoProducts,
                seoSponsoredProductsAttrValue,
                isSalesPage,
              ));
          }

          /**
           * Filter the personalized pinned content if exist, based on the active filters.
           */
          const resolvedPinnedContent =
            pinnedContent &&
            pageNumber === 0 &&
            this.productListUIService.hasPersonalizedContent(pinnedContent)
              ? this.productListUIService.filterPersonalizedPinnedContent(
                  pinnedContent,
                  filters,
                  selectedStoreInfo,
                )
              : pinnedContent;

          const resolvedProductDetails = productDetails ?? {};

          const dpld = this.buildDisplayableProductList(
            {
              productVOs,
              pinnedContent: resolvedPinnedContent,
              sponsoredProducts,
              filters,
              pageNumber,
              productDetails: resolvedProductDetails,
              sponsoredPinnedContent,
            },
            featureFlags,
            adService,
            criteoGridOnLoadBeacon,
            seoSponsoredProductsAttrValue,
            saleExcludedProductIds,
          );

          return { dpld, onlyCriteoChanged, productListFromTransferState };
        },
      ),
      tap(({ dpld, onlyCriteoChanged, productListFromTransferState }) => {
        if (
          (!onlyCriteoChanged || this.plpMlService.plpListIsFirstRender()) &&
          !productListFromTransferState
        ) {
          this.plpMlService.sendSearchEventData(dpld);
          this.plpMlService.plpListIsFirstRender.set(false);
        }
      }),
      switchMap(({ dpld }) =>
        of(displayableProductListBuilt({ displayableProductListData: dpld })),
      ),
    ),
  );

  /**
   * Effect to update Criteo products.
   * Triggers whenever the resolvers finish initialization or when the product list is refreshed.
   */
  updateCriteoProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        plpResolverAPIDataInitialized,
        srlpResolverAPIDataInitialized,
        productListLoadTriggered,
      ),
      concatLatestFrom(() => [
        this.store.select(selectFeatureFlags),
        this.store.select(selectProductListSEOAttributes),
        this.store.select(selectSelectedFilters),
        this.store.select(selectPageSize),
        this.store.select(selectPageNumber),
        this.store.select(selectSearchVO),
      ]),
      switchMap(
        ([, featureFlags, seoAttributes, selectedFilters, pageSize, pageNumber, searchVO]) => {
          const productType = seoAttributes
            ? ProductTypes.VIEW_CATEGORY
            : ProductTypes.VIEW_SEARCH_RESULT;

          const breadcrumbValue = seoAttributes?.BREADCRUMB?.value
            ? this.criteoService.transformToCriteoBreadcrumbFormat(seoAttributes.BREADCRUMB.value)
            : undefined;

          const category =
            breadcrumbValue ??
            (featureFlags.plpDmnAdServerEnabled && searchVO?.selectedCategory
              ? this.criteoService.transformToCriteoBreadcrumbFormat(searchVO.selectedCategory)
              : undefined);

          const categoryOrSearchTerm: string | undefined = category ?? searchVO?.searchTerm;

          if (
            isPlatformServer(this.platformId) ||
            !featureFlags.isUsingCriteoSPAAdServer ||
            !categoryOrSearchTerm
          ) {
            return of(criteoProductsResolvedEmpty());
          }

          return (
            productType === ProductTypes.VIEW_CATEGORY
              ? this.dmnAdService.getPlpAds({
                  category: categoryOrSearchTerm,
                  listSize: pageSize,
                  pageNumber,
                  filters: selectedFilters,
                })
              : this.dmnAdService.getSrlpAds({
                  searchTerm: categoryOrSearchTerm,
                  listSize: pageSize,
                  pageNumber,
                  filters: selectedFilters,
                })
          ).pipe(
            map((criteoProducts) => {
              const placements = criteoProducts?.placements;
              const carousel = placements?.carousel?.[0];
              if (
                carousel?.products?.length &&
                carousel.products.length < CRITEO_CAROUSEL_MINIMUM_PRODUCTS
              ) {
                carousel.products = [];
              } else if (carousel?.OnLoadBeacon) {
                const excluded = this.dmnAdService.buildExcludedCriteoProductsParam(
                  carousel.products,
                );
                this.dmnAdService.sendBeacon(
                  carousel.OnLoadBeacon,
                  this.beaconQueryParameters,
                  excluded,
                );
              }
              return criteoProductsFetchSuccessful({ criteoProducts });
            }),
            catchError((err) => {
              this.logger.error('Error fetching Criteo data', err);
              return of(criteoProductsFetchErrored());
            }),
          );
        },
      ),
    ),
  );

  /**
   * Effect to update the pinned content.
   * Triggers whenever the resolvers finish initialization.
   */
  updatePinnedContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(plpResolverAPIDataInitialized, srlpResolverAPIDataInitialized),
      concatLatestFrom(() => [
        this.store.select(selectFeatureFlags),
        this.store.select(selectSearchVO),
        this.store.select(selectPageType),
        this.store.select(selectProductListSEO),
      ]),
      switchMap(([, featureFlags, searchVO, pageType, productListSEO]) => {
        const seoUrlToken: string | undefined =
          pageType === 'plp' ? productListSEO?.seoUrl : searchVO?.searchTerm?.replaceAll(' ', '_');

        if (
          isPlatformServer(this.platformId) ||
          !featureFlags.showPinnedContent ||
          (searchVO?.pageNumber ?? 0) > 0 ||
          !seoUrlToken
        ) {
          return of(pinnedContentResolvedEmpty());
        }

        // Typing this explicitly so typescript doesn't complain
        const pinnedContentFetch: Observable<
          HomrPage<'pinnedA'> | AemPinnedContentResponse | null
        > = featureFlags.useAEMForPCS
          ? this.productListAemService.getPinnedContentData$({
              pageType: 'pinned-content',
              name: seoUrlToken,
            })
          : (this.homrDataService.getData$('pinned-content', 2, seoUrlToken) as Observable<
              HomrPage<'pinnedA'>
            >);

        return pinnedContentFetch.pipe(
          timeout(10000),
          take(1),
          switchMap((pinnedContent) => {
            let pinnedContentSpots: PinnedContent = {};
            if (featureFlags.useAEMForPCS) {
              pinnedContentSpots = this.productListAemService.pinnedContentAemToHomrTranslator(
                pinnedContent as AemPinnedContentResponse | null,
              );
            } else {
              const pinnedContentData = (pinnedContent as HomrPage<'pinnedA'>)?.['content']?.[
                'pinned-content'
              ];
              const csid: string | undefined = (pinnedContent as HomrPage<'pinnedA'>)?.[
                'contentScheduleMeta'
              ]?.['pinned-content']?.csid;

              if (Array.isArray(pinnedContentData)) {
                pinnedContentData.forEach((spot) => {
                  const index = spot.pinnedPosition - 1;
                  pinnedContentSpots[index] = spot;
                  if (csid !== undefined) {
                    pinnedContentSpots[index].csid = csid;
                  }
                });
              }
            }
            return from(
              this.featureConfigurationService.getPersonalization(
                PersonalizationDataKeys.MODIFY_PCS,
                pinnedContentSpots,
              ),
            ).pipe(catchError(() => of(pinnedContentSpots)));
          }),
          map((personalizedPinnedContent) => {
            return pinnedContentFetchSuccessful({ pinnedContent: personalizedPinnedContent ?? {} });
          }),
          catchError((err) => {
            this.logger.error('Error fetching pinned content data', err);
            return of(pinnedContentFetchErrored());
          }),
        );
      }),
    ),
  );

  /**
   * @description Fetches PZ PLP banner personalization data.
   * Listens to productListLoadSuccessful and plpResolverAPIDataInitialized.
   * Only runs on PLP pages (pageType === 'plp') and on the first page (pageNumber === 0).
   * Uses debounceTime(0) to collapse rapid-fire triggers into a single emission.
   * Applies filterPersonalizedBanners using the active selected filters and store info.
   * Dispatches pzPlpBannerFetchSuccessful or pzPlpBannerResolvedEmpty.
   */
  pzPlpBannerFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productListLoadSuccessful, plpResolverAPIDataInitialized),
      debounceTime(0),
      concatLatestFrom(() => [
        this.store.select(selectPageNumber),
        this.store.select(selectSelectedFilters),
        this.store.select(selectUserStoreInfo),
        this.store.select(selectPageType),
      ]),
      switchMap(([, pageNumber, filters, selectedStoreInfo, pageType]) => {
        if (isPlatformServer(this.platformId) || pageType !== 'plp' || (pageNumber ?? 0) !== 0) {
          return of(pzPlpBannerResolvedEmpty());
        }
        return defer(() =>
          this.featureConfigurationService.getPersonalization(
            PersonalizationDataKeys.MODIFY_PLP_BANNER,
            null,
            { timeout: 5000 },
          ),
        ).pipe(
          map((data) => {
            const bannerData = data as { banners?: PzPlpBannerItem[] } | null;
            const rawBanners = bannerData?.banners;
            if (!Array.isArray(rawBanners) || rawBanners.length === 0) {
              return pzPlpBannerResolvedEmpty();
            }
            const pzPlpBanners = this.productListUIService.filterPersonalizedBanners(
              rawBanners,
              filters ?? {},
              selectedStoreInfo,
            );
            if (pzPlpBanners.length === 0) {
              return pzPlpBannerResolvedEmpty();
            }
            return pzPlpBannerFetchSuccessful({ pzPlpBanners });
          }),
        );
      }),
    ),
  );

  /**
   * @description Sets the query parameters and navigates to the updated URL.
   * Skips navigation entirely when the computed params match the current URL params.
   * Uses replaceUrl=true when all changes are resetting values to null (restoring
   * defaults), avoiding unnecessary browser history entries.
   */
  private setQueryParamsAndNavigate(
    pageNumber: number,
    pageSize: number,
    filters: Record<string, string[]>,
    selectedSort: { label: string; value: number },
    productListSEO: PrunedProductListSEO | null,
    pageType: CommonPageType | '',
    selectedStore: HeaderStoreSelection | null,
  ): void {
    const defaultSort = this.getDefaultSelectedSort(productListSEO, pageType);

    const nextQueryParams: Record<string, string | number | null> = {
      pageNumber: pageNumber || null,
      pageSize: pageSize === DEFAULT_PAGE_SIZE ? null : pageSize,
      filterFacets: Object.keys(filters).length ? this.generateFilterString(filters) : null,
      selectedSort: selectedSort.value === defaultSort ? null : selectedSort.value,
      selectedStore: pageType === 'srlp' ? selectedStore?.store || null : null,
    };

    const currentQueryParams = this.router.routerState.snapshot.root.queryParams;
    const trackedKeys = [
      'pageNumber',
      'pageSize',
      'filterFacets',
      'selectedSort',
      'selectedStore',
    ] as const;

    const hasChanges = trackedKeys.some(
      (key) =>
        this.normalizeQueryParam(nextQueryParams[key]) !==
        this.normalizeQueryParam(currentQueryParams[key]),
    );

    if (!hasChanges) {
      return;
    }

    // replaceUrl=true only when every changed key is transitioning to null from a default value.
    const replaceUrl = trackedKeys.every((key) => {
      const next = this.normalizeQueryParam(nextQueryParams[key]);
      const current = this.normalizeQueryParam(currentQueryParams[key]);
      let currentIsDefault = false;

      switch (key) {
        case 'pageNumber':
          currentIsDefault = current === '0';
          break;
        case 'pageSize':
          currentIsDefault = current === String(DEFAULT_PAGE_SIZE);
          break;
        case 'selectedSort':
          currentIsDefault = current === String(defaultSort);
          break;
        case 'filterFacets':
          currentIsDefault = current === '';
          break;
        case 'selectedStore':
          currentIsDefault = current === '';
          break;
      }

      // null = not even in the url, currentIsDefault = in the url but at the default value
      return (current === null || currentIsDefault) && next === null;
    });

    this.router.navigate([], {
      queryParams: nextQueryParams,
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  private getDefaultSelectedSort(
    productListSEO: PrunedProductListSEO | null,
    pageType: CommonPageType | '',
  ): number {
    if (pageType === 'plp' && productListSEO?.longDescription) {
      return plpDefaultSortMap[productListSEO.longDescription] ?? 0;
    }
    return 0;
  }

  /**
   * @description Normalizes a query param value for comparison.
   * Collapses null, undefined, and empty string to null so that router string
   * params and our numeric/null values can be compared consistently.
   */
  private normalizeQueryParam(value: string | number | null | undefined): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return String(value);
  }

  /**
   * Checks the availability of a list of products in a specific store.
   *
   * @param productVOs - An array of ProductVO objects representing the products to check.
   * @param criteoProducts - An array of Criteo products to include in the availability check.
   * @param store - The store information including zipcode and store location.
   * @returns An Observable that emits a ProductAvailabilityCollection, null, or an empty array in case of an error.
   */
  private productAvailability(
    productVOs: ProductVO[],
    criteoProducts: CriteoViewProductsResponse | null,
    store: HeaderStoreSelection | null,
  ): Observable<ProductAvailabilityCollection | null | []> {
    if (isPlatformServer(this.platformId)) {
      return of(null);
    }

    const regularEcodes: string[] = productVOs?.length
      ? productVOs
          .filter((item) => item?.parentPartnumber)
          .map((item) => item.parentPartnumber)
          .filter((ecode) => ecode)
      : [];

    const criteoEcodes: string[] = criteoProducts?.placements?.inGrid?.[0]?.products?.length
      ? criteoProducts.placements.inGrid[0].products
          .map((product) => product.ParentSKU)
          .filter((ecode) => ecode)
      : [];

    const allEcodes = [...new Set([...regularEcodes, ...criteoEcodes])];

    if (!allEcodes.length) {
      return of(null);
    }

    return this.productCatalogService
      .getAvailabilityData(allEcodes, true, store?.zipcode, store?.store)
      .pipe(catchError((): Observable<[]> => of([])));
  }

  private productPromotion(
    productVOs: ProductVO[],
  ): Observable<ProductPromotionCollection | ProductPromotionResponse[] | null> {
    if (!isPlatformBrowser(this.platformId) || !productVOs?.length) {
      return of(null);
    }

    return this.discountEligibilityService.getPromoList$(productVOs);
  }

  /**
   * @description set up params and make product list call
   *
   * searchTermOverride is used for the "Search Instead" functionality on the SRLP, when we want to
   * trigger a search with the original search term, not the "corrected" one in the current SearchVO
   */
  private updateProductList(
    plc: ProductListCore,
    filters: Record<string, string[]>,
    selectedStoreInfo: HeaderStoreSelection | null,
    fulfillmentFacetsEnabled = false,
    searchTermOverride?: string,
  ): Observable<ProductList | null> {
    // check if effect was triggered by a page change, and if so set the new page number for the call
    // otherwise reset the page number to zero so that we get the first page of filtered results
    const pageNumber = plc.pageNumber || 0;
    const pageSize = plc.pageSize || DEFAULT_PAGE_SIZE;
    const catId = plc.productListSEO?.catgroupId;

    let selectedStoreNumber = '';
    let zipcode = '';

    const includeFulfillmentFacets = fulfillmentFacetsEnabled;

    if (selectedStoreInfo) {
      selectedStoreNumber = selectedStoreInfo.store || '';
      zipcode = selectedStoreInfo.zipcode || '';
    }

    const searchTerm = catId
      ? null
      : searchTermOverride || plc.productList?.searchVO?.searchTerm || null;

    const params = this.productListApiService.setProductListParamDataForApi({
      selectedCategoryId: catId || null,
      searchTerm,
      pageNumber,
      selectedFilters: filters,
      pageSize,
      includeFulfillmentFacets,
      selectedStoreNumber,
      zipcode,
    });

    return this.productListApiService.getProductListDataFromApi$(params, 5).pipe(
      catchError((error) => {
        this.productListApiService.logProductListApiError(error, 'plp-effects', {
          searchTerm,
          catgroupId: catId,
        });
        return of(null);
      }),
    );
  }

  private generateFilterString(filters: Record<string, string[]>): string | null {
    const keys = Object.keys(filters);
    if (keys.length === 0) {
      return null;
    }

    // Build the string we will use to add to the URL
    let filtersString = '';
    keys.forEach((key, index) => {
      const filterList = filters[key].join();
      const separator = index === keys.length - 1 ? '' : ';';
      filtersString += `${key}:${filterList}${separator}`;
    });

    return filtersString;
  }

  private buildDisplayableProductList(
    {
      productVOs,
      pinnedContent,
      sponsoredProducts,
      filters,
      pageNumber,
      productDetails,
      sponsoredPinnedContent,
    }: DisplayableProductListParams,
    featureFlags: Record<string, boolean>,
    adService: CriteoService | DmnAdService,
    gridLoadBeacon?: string,
    sponsoredProductsSEO?: string,
    saleExcludedProductIds: string[] = [],
  ): DisplayableProductListData {
    if (!productVOs?.length) {
      return [];
    }

    // Use sets for duplicate checking
    const processedPartNumbers = new Set<string>();
    const processedECodes = new Set<string>();
    const skippedSponsoredProductsIDs: string[] = [...saleExcludedProductIds];
    const dpld: DisplayableProductListData = [];

    // build a map of product details by parent partnumber for quick lookup
    const productDetailsByParentPartnumber = this.buildProductDetailsByParentPartnumber(
      productVOs,
      productDetails,
    );

    // Convert to simple arrays
    const sponsoredQueue = Array.isArray(sponsoredProducts) ? sponsoredProducts : [];
    let sponsoredIndex = 0;
    let productIndex = 0;
    let productCardCount = 0;

    // Single pass through products with index-based iteration
    while (productIndex < productVOs.length || sponsoredIndex < sponsoredQueue.length) {
      const position = dpld.length;

      // Add pinned content for first page
      if (pinnedContent?.[position] && pageNumber === 0) {
        dpld.push(pinnedContent[position]);
        continue;
      }

      // Check if we should add a sponsored product
      if (
        sponsoredIndex < sponsoredQueue.length &&
        this.criteoService.isSponsoredProduct(
          [sponsoredQueue[sponsoredIndex]],
          productCardCount,
          pageNumber,
          filters,
          featureFlags,
          pinnedContent,
        )
      ) {
        const currentProduct = sponsoredQueue[sponsoredIndex++];
        const wasSponsoredProductAdded = this.addSponsoredProductCardIfEligible({
          currentProduct,
          productDetailsByParentPartnumber,
          productVOs,
          productIndex,
          processedPartNumbers,
          processedECodes,
          skippedSponsoredProductsIDs,
          dpld,
        });
        productCardCount = this.getUpdatedProductCardCount(
          productCardCount,
          wasSponsoredProductAdded,
        );
      } else if (productIndex < productVOs.length) {
        const currentProduct = productVOs[productIndex++];
        const wasRegularProductAdded = this.addRegularProductCardIfEligible({
          currentProduct,
          productDetails,
          processedPartNumbers,
          processedECodes,
          dpld,
        });
        productCardCount = this.getUpdatedProductCardCount(
          productCardCount,
          wasRegularProductAdded,
        );
      } else {
        // No more products to process
        break;
      }
    }

    if (sponsoredPinnedContent) {
      dpld.splice(CRITEO_DIGITAL_SHELF_TALKER_POSITION, 0, sponsoredPinnedContent);
    }

    // Logic for firing inGrid onLoadBeacon if we are showing Sponsored Products
    adService.checkOnLoadBeaconFiring(
      sponsoredProductsSEO ?? '',
      gridLoadBeacon ?? '',
      sponsoredQueue.slice(sponsoredIndex),
      skippedSponsoredProductsIDs,
    );
    this.checkPlatformServer(dpld);

    return dpld;
  }

  private addSponsoredProductCardIfEligible({
    currentProduct,
    productDetailsByParentPartnumber,
    productVOs,
    productIndex,
    processedPartNumbers,
    processedECodes,
    skippedSponsoredProductsIDs,
    dpld,
  }: {
    currentProduct: PlpInGridSponsoredProduct;
    productDetailsByParentPartnumber: Map<string, ProductDetail>;
    productVOs: ProductVO[];
    productIndex: number;
    processedPartNumbers: Set<string>;
    processedECodes: Set<string>;
    skippedSponsoredProductsIDs: string[];
    dpld: DisplayableProductListData;
  }): boolean {
    const sponsoredProductId = currentProduct.product_id;
    const hasSponsoredProductId =
      typeof sponsoredProductId === 'string' && sponsoredProductId.length > 0;
    const sponsoredProductItemId = currentProduct.product?.ID;

    if (!hasSponsoredProductId) {
      if (sponsoredProductItemId) {
        skippedSponsoredProductsIDs.push(sponsoredProductItemId);
      }
      return false;
    }

    if (processedECodes.has(sponsoredProductId)) {
      if (sponsoredProductItemId) {
        skippedSponsoredProductsIDs.push(sponsoredProductItemId);
      }
      return false;
    }

    const sponsoredProductWithDetails = this.attachSkuCountAndPriceIndicatorToSponsoredProducts(
      currentProduct,
      productDetailsByParentPartnumber.get(sponsoredProductId),
    );
    dpld.push(sponsoredProductWithDetails);
    processedECodes.add(sponsoredProductId);

    this.markDuplicatesFromIndex(
      productVOs,
      productIndex,
      sponsoredProductId,
      processedPartNumbers,
    );

    return true;
  }

  private addRegularProductCardIfEligible({
    currentProduct,
    productDetails,
    processedPartNumbers,
    processedECodes,
    dpld,
  }: {
    currentProduct: ProductVO;
    productDetails: Record<string, ProductDetail>;
    processedPartNumbers: Set<string>;
    processedECodes: Set<string>;
    dpld: DisplayableProductListData;
  }): boolean {
    if (
      !this.isValidProduct(currentProduct, productDetails) ||
      processedPartNumbers.has(currentProduct.partnumber)
    ) {
      return false;
    }

    processedPartNumbers.add(currentProduct.partnumber);
    processedECodes.add(currentProduct.parentPartnumber);

    const currentProductWithDetails = this.attachProductDetailsToProductList(
      currentProduct,
      productDetails,
    );

    dpld.push(currentProductWithDetails);
    return true;
  }

  private getUpdatedProductCardCount(currentCount: number, wasCardAdded: boolean): number {
    return wasCardAdded ? currentCount + 1 : currentCount;
  }

  private buildProductDetailsByParentPartnumber(
    productVOs: ProductVO[],
    productDetails: Record<string, ProductDetail>,
  ): Map<string, ProductDetail> {
    const detailsByParentPartnumber = new Map<string, ProductDetail>();

    for (const product of productVOs) {
      const parentPartnumber = product.parentPartnumber;
      const hasParentPartnumber =
        typeof parentPartnumber === 'string' && parentPartnumber.length > 0;
      const isAlreadyMapped = hasParentPartnumber
        ? detailsByParentPartnumber.has(parentPartnumber)
        : false;

      if (hasParentPartnumber && isAlreadyMapped === false) {
        const productId = product.parentCatentryId || product.catentryId;
        const detail = productDetails[String(productId)];
        if (detail) {
          detailsByParentPartnumber.set(parentPartnumber, detail);
        }
      }
    }

    return detailsByParentPartnumber;
  }

  private attachSkuCountAndPriceIndicatorToSponsoredProducts(
    sponsoredProduct: PlpInGridSponsoredProduct,
    productDetail?: ProductDetail,
  ): PlpInGridSponsoredProduct {
    if (sponsoredProduct.product === undefined) {
      return sponsoredProduct;
    }

    return {
      ...sponsoredProduct,
      product: {
        ...sponsoredProduct.product,
        ...(productDetail ? { SkuCount: productDetail.skuCount } : {}),
        Color: sponsoredProduct.product.Color ?? [],
        priceIndicator: this.calcSponsoredPriceIndicator(sponsoredProduct.product),
      },
    };
  }

  private calcSponsoredPriceIndicator(product: PlpDisplayableProduct): 0 | 1 {
    const isOnSale =
      this.isSalePrice(product.maxofferprice, product.maxlistprice) ||
      this.isSalePrice(product.minofferprice, product.minlistprice);

    return isOnSale ? 0 : 1;
  }

  private isSalePrice(offerPrice?: string, listPrice?: string): boolean {
    // Prices aren't guaranteed to exist; only compare when both are present.
    if (!offerPrice || !listPrice) {
      return false;
    }

    return Number(offerPrice) < Number(listPrice);
  }

  /**
   * Marks duplicate products from a starting index
   */
  private markDuplicatesFromIndex(
    productVOs: ProductVO[],
    startIndex: number,
    sponsoredProductId: string,
    processedPartNumbers: Set<string>,
  ): void {
    // Check next 5 products for duplicates
    const endIndex = Math.min(startIndex + 5, productVOs.length);
    for (let i = startIndex; i < endIndex; i++) {
      if (productVOs[i].parentPartnumber === sponsoredProductId) {
        processedPartNumbers.add(productVOs[i].partnumber);
      }
    }
  }

  private checkPlatformServer(dpld: DisplayableProductListData): void {
    if (isPlatformServer(this.platformId)) {
      this.state.set(DPLD_KEY, dpld);
    }
  }

  private plpOrSrlpKillswitch(pageType: string, featureFlags: Record<string, boolean>): boolean {
    if (pageType === 'plp') {
      return featureFlags.plpDmnAdServerEnabled;
    } else if (pageType === 'srlp') {
      return featureFlags.srlpDmnAdServerEnabled;
    } else {
      return false;
    }
  }

  private attachProductDetailsToProductList(
    product: ProductVO,
    productDetails: Record<string, ProductDetail>,
  ) {
    const id = product.parentCatentryId ? product.parentCatentryId : product.catentryId;
    const { priceIndicator, mapPriceIndicator, prices, skuAttributes, skuCount } =
      productDetails[id];

    return {
      ...product,
      priceIndicator,
      mapPriceIndicator,
      prices,
      skuAttributes,
      skuCount,
    };
  }

  /**  @description throw console warns to indicate why a product is not displaying in the grid */
  private isValidProduct(
    product: ProductVO,
    productDetails: Record<string, ProductDetail>,
  ): boolean {
    if (!product.parentCatentryId && !product.catentryId) {
      this.logger.error('Product is Missing:  parentCatentryId and catentryId', product);
      return false;
    } else if (!product.partnumber || !product.parentPartnumber) {
      this.logger.error(
        `Product is Missing: ${!product.partnumber ? 'partnumber' : ''} ${
          !product.parentPartnumber ? 'parentPartnumber' : ''
        }`,
        product,
      );
      return false;
    } else if (!product.name) {
      this.logger.error('Product is Missing: name', product);
      return false;
    }
    const id = product.parentCatentryId ? product.parentCatentryId : product.catentryId;
    const productDetail = productDetails[id];
    if (
      productDetail?.mapPriceIndicator === undefined ||
      productDetail?.priceIndicator === undefined ||
      productDetail?.prices.maxlistprice === undefined ||
      productDetail?.prices.minlistprice === undefined ||
      productDetail?.prices.maxofferprice === undefined ||
      productDetail?.prices.minofferprice === undefined ||
      productDetail?.skuCount === undefined
    ) {
      this.logger.error(
        'ProductDetail does not have a required field and will not be displayed. Missing one of: productDetail, mapPriceIndicator, priceIndicator, prices',
        product,
      );
      return false;
    }
    return true;
  }

  private prepareCriteoGridProducts(
    adService: CriteoService | DmnAdService,
    criteoProducts: CriteoViewProductsResponse,
    seoSponsoredProductsAttrValue: string,
    isSalesPage: boolean,
  ) {
    const criteoInGrid = criteoProducts?.placements.inGrid?.[0];
    const criteoInGridProducts = criteoInGrid?.products;
    let sponsoredProducts: PlpInGridSponsoredProduct[] | null = criteoInGridProducts
      ? this.criteoService.transformCriteoInGridProducts(criteoInGridProducts, isSalesPage)
      : null;
    const criteoGridOnLoadBeacon = criteoInGrid?.OnLoadBeacon;
    const saleExcludedProductIds: string[] = [];
    if (seoSponsoredProductsAttrValue === 'N' && sponsoredProducts?.length) {
      const allSponsoredProductsIDs = sponsoredProducts.flatMap((prod) =>
        'product' in prod && prod.product?.ID ? [prod.product.ID] : [],
      );
      const sponProdIDsString = allSponsoredProductsIDs.join('|');
      adService.fireInGridOnLoadBeacon(criteoGridOnLoadBeacon, sponProdIDsString);
      sponsoredProducts = null;
    } else if (sponsoredProducts?.length) {
      const renderableSponsoredProducts = [];
      for (const sponsoredProduct of sponsoredProducts) {
        if (sponsoredProduct.excludeForSale) {
          if (sponsoredProduct.product?.ID) {
            saleExcludedProductIds.push(sponsoredProduct.product.ID);
          }
        } else {
          renderableSponsoredProducts.push(sponsoredProduct);
        }
      }
      sponsoredProducts = renderableSponsoredProducts;
    }
    return { sponsoredProducts, criteoGridOnLoadBeacon, saleExcludedProductIds };
  }

  private prepareSponsoredPinnedContent(
    adService: CriteoService | DmnAdService,
    dst: CriteoDigitalShelfTalker,
  ): CriteoPinnedContentSpot | null {
    const renderableSponsoredPinnedContent = this.criteoService.transformToPinnedContent(dst);
    if (renderableSponsoredPinnedContent) {
      adService.fireDSTOnLoadBeacon(dst.OnLoadBeacon);
    }
    return renderableSponsoredPinnedContent;
  }
}
